import { supabase } from '../lib/supabaseClient';
import type {
  AttendanceRecord, AuthorizationRecord, PayrollLineCalc,
} from '../domain/payroll';

export interface ClosureWorker {
  id: string;
  dni: string;
  firstNames: string;
  lastNames: string;
}

export interface ClosureData {
  records: AttendanceRecord[];
  workers: Record<string, ClosureWorker>;
}

/**
 * PostgREST corta cada respuesta en 1000 filas ("Max rows" del proyecto) SIN error.
 * Un cierre mensual con 40 recursos son ~2.000 marcados: sin paginar, el cierre
 * —que después queda inmutable— PAGARÍA DE MENOS. Todas las lecturas de este módulo
 * paginan con `.range()` y contrastan lo recibido contra `count: 'exact'`.
 */
const PAGE_SIZE = 1000;

/** Asistencias del periodo listas para `computePayroll`, más el directorio de nombres. */
export async function listAttendanceForClosure(
  campaignId: string, start: string, end: string,
): Promise<ClosureData> {
  type Raw = {
    id: string;
    kind: 'ingreso' | 'salida';
    device_ts: string;
    status: 'pendiente' | 'validada' | 'observada';
    origin: 'online' | 'offline_sync';
    assignment: {
      worker_id: string;
      worker: { id: string; dni: string; first_names: string; last_names: string } | null;
      position: {
        id: string; date: string; start_time: string; end_time: string;
        rate_amount: number | string; rate_unit: 'jornada' | 'hora';
      };
    };
  };

  const raw: Raw[] = [];
  let total: number | null = null;
  for (let from = 0; ; from += PAGE_SIZE) {
    // No se filtra por `origin` ni por `validation_note`: las salidas fijadas a mano por
    // el coordinador (RPC `set_manual_exit`: `origin = 'offline_sync'`, `status = 'validada'`)
    // entran al cálculo como cualquier otra salida. Hint de FK obligatorio: `assignments`
    // tiene tres FK a `profiles` (worker_id, assigned_by, approved_by).
    const { data, error, count } = await supabase
      .from('attendance')
      .select(
        `id, kind, device_ts, status, origin,
        assignment:assignments!inner(worker_id,
          worker:profiles!assignments_worker_id_fkey(id, dni, first_names, last_names),
          position:positions!inner(id, date, campaign_id, start_time, end_time,
                                   rate_amount, rate_unit))`,
        { count: 'exact' },
      )
      .eq('assignment.position.campaign_id', campaignId)
      .gte('assignment.position.date', start)
      .lte('assignment.position.date', end)
      .order('id')
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    if (count !== null) total = count;
    const batch = (data ?? []) as unknown as Raw[];
    raw.push(...batch);
    if (batch.length < PAGE_SIZE) break;
  }
  if (total !== null && raw.length !== total) {
    throw new Error(
      `Lectura incompleta de asistencias: se recibieron ${raw.length} de ${total} filas. ` +
      'NO crees el cierre con estos datos: acorta el periodo y vuelve a calcular.',
    );
  }

  const records: AttendanceRecord[] = [];
  const workers: Record<string, ClosureWorker> = {};
  for (const row of raw) {
    const w = row.assignment.worker;
    const workerId = row.assignment.worker_id;
    if (w && !workers[workerId]) {
      workers[workerId] = {
        id: workerId, dni: w.dni, firstNames: w.first_names, lastNames: w.last_names,
      };
    }
    records.push({
      workerId,
      positionId: row.assignment.position.id,
      date: row.assignment.position.date,
      // El horario programado es el denominador de la regla del medio día.
      startTime: row.assignment.position.start_time,
      endTime: row.assignment.position.end_time,
      rateAmount: Number(row.assignment.position.rate_amount),
      rateUnit: row.assignment.position.rate_unit,
      kind: row.kind,
      deviceTs: row.device_ts,
      status: row.status,
    });
  }
  return { records, workers };
}

/**
 * Trabajadores con asignación APROBADA en el periodo de esa campaña, con sus nombres.
 * `computePayroll` los recibe como semilla (`periodWorkerIds`) para que quien no marcó
 * nada —porque toda su novedad fue una falta o permiso aprobado— igual salga en el
 * cierre con 0 días, 0 monto y su nota "Falta justificada el …".
 */
export async function listPeriodWorkers(
  campaignId: string, start: string, end: string,
): Promise<ClosureWorker[]> {
  type Raw = {
    id: string;
    worker_id: string;
    worker: { id: string; dni: string; first_names: string; last_names: string } | null;
  };

  const raw: Raw[] = [];
  let total: number | null = null;
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error, count } = await supabase
      .from('assignments')
      .select(
        `id, worker_id,
        worker:profiles!assignments_worker_id_fkey(id, dni, first_names, last_names),
        position:positions!inner(campaign_id, date)`,
        { count: 'exact' },
      )
      .eq('status', 'aprobada')
      .eq('position.campaign_id', campaignId)
      .gte('position.date', start)
      .lte('position.date', end)
      .order('id')
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    if (count !== null) total = count;
    const batch = (data ?? []) as unknown as Raw[];
    raw.push(...batch);
    if (batch.length < PAGE_SIZE) break;
  }
  if (total !== null && raw.length !== total) {
    throw new Error(
      `Lectura incompleta de asignaciones: se recibieron ${raw.length} de ${total} filas. ` +
      'NO crees el cierre con estos datos: acorta el periodo y vuelve a calcular.',
    );
  }

  const byId = new Map<string, ClosureWorker>();
  for (const r of raw) {
    if (byId.has(r.worker_id)) continue;
    byId.set(r.worker_id, {
      id: r.worker_id,
      dni: r.worker?.dni ?? '',
      firstNames: r.worker?.first_names ?? '',
      lastNames: r.worker?.last_names ?? '',
    });
  }
  return [...byId.values()];
}

/**
 * Autorizaciones aprobadas del periodo de los trabajadores con asignación aprobada
 * en esa campaña. `workerIds` evita repetir la consulta cuando el llamador ya pidió
 * `listPeriodWorkers`. Devuelve [] si la tabla `authorizations` aún no existe.
 */
export async function listAuthorizationsForClosure(
  campaignId: string, start: string, end: string, workerIds?: string[],
): Promise<AuthorizationRecord[]> {
  const ids = workerIds ?? (await listPeriodWorkers(campaignId, start, end)).map((w) => w.id);
  if (ids.length === 0) return [];

  type Raw = {
    worker_id: string;
    kind: 'tardanza' | 'falta' | 'permiso';
    event_date: string;
    status: 'pendiente' | 'aprobado' | 'rechazado';
  };

  // Paginada igual que las demás lecturas del cierre: 40 recursos con varias
  // justificaciones al mes caben de sobra en una página, pero un periodo largo no.
  const raw: Raw[] = [];
  let total: number | null = null;
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error, count } = await supabase
      .from('authorizations')
      .select('worker_id, kind, event_date, status', { count: 'exact' })
      .in('worker_id', ids)
      .eq('status', 'aprobado')
      .gte('event_date', start)
      .lte('event_date', end)
      .order('id')
      .range(from, from + PAGE_SIZE - 1);
    if (error) {
      if (error.code === '42P01') {
        console.warn('Tabla authorizations aún no creada; el cierre no incluirá justificaciones.');
        return [];
      }
      throw error;
    }
    if (count !== null) total = count;
    const batch = (data ?? []) as unknown as Raw[];
    raw.push(...batch);
    if (batch.length < PAGE_SIZE) break;
  }
  if (total !== null && raw.length !== total) {
    throw new Error(
      `Lectura incompleta de autorizaciones: se recibieron ${raw.length} de ${total} filas. ` +
      'NO crees el cierre con estos datos: acorta el periodo y vuelve a calcular.',
    );
  }

  return raw.map((r) => ({
    workerId: r.worker_id, kind: r.kind, eventDate: r.event_date, status: r.status,
  }));
}

/** Crea el cierre en estado `borrador` con su snapshot de líneas. Devuelve el id. */
/**
 * Feriados del periodo con su multiplicador (`holidays`, 0077).
 *
 * Se lee por periodo y no completa: un cierre de agosto no tiene por qué cargar los feriados de
 * 2027. La clave es la fecha `'YYYY-MM-DD'` tal como la guarda Postgres, que es la misma forma con
 * la que `computePayroll` compara `positions.date`.
 */
export async function listHolidays(start: string, end: string): Promise<Map<string, number>> {
  const { data, error } = await supabase
    .from('holidays')
    .select('fecha, multiplicador')
    .gte('fecha', start)
    .lte('fecha', end);
  if (error) throw error;
  const out = new Map<string, number>();
  for (const r of (data ?? []) as { fecha: string; multiplicador: number | string }[]) {
    const m = Number(r.multiplicador);
    // Un multiplicador ilegible se ignora en vez de convertirse en NaN: un NaN acá pondría el
    // monto de ese día en NaN y el cierre entero saldría en blanco sin decir por qué.
    if (Number.isFinite(m) && m > 0) out.set(r.fecha, m);
  }
  return out;
}

/**
 * Quién no cobra el bono en esta campaña y periodo aunque los números le den (`bono_asistencia_excluido`, 0403):
 * trabajador → motivo. Vale si el tramo de la exclusión toca el periodo del cierre.
 */
export async function listSinBono(campaignId: string, start: string, end: string): Promise<Map<string, string>> {
  const { data, error } = await supabase
    .from('bono_asistencia_excluido')
    .select('worker_id, motivo')
    .eq('campaign_id', campaignId)
    .lte('desde', end)
    .gte('hasta', start);
  if (error) throw error;
  return new Map(((data ?? []) as { worker_id: string; motivo: string }[]).map((r) => [r.worker_id, r.motivo]));
}

/**
 * Días ASIGNADOS por trabajador en el periodo. Es el denominador del bono: no es lo mismo no
 * faltar teniendo 27 días que no faltar teniendo 1.
 *
 * Cuenta **fechas distintas**, no asignaciones: si alguien tuviera dos posiciones el mismo día,
 * ese día es UN día de asistencia y contarlo dos veces le negaría el bono para siempre.
 */
export async function listAssignedDays(
  campaignId: string, start: string, end: string,
): Promise<Map<string, number>> {
  type Raw = { worker_id: string; position: { date: string } | null };
  const raw: Raw[] = [];
  let total: number | null = null;
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error, count } = await supabase
      .from('assignments')
      .select('worker_id, position:positions!inner(campaign_id, date)', { count: 'exact' })
      .eq('status', 'aprobada')
      .eq('position.campaign_id', campaignId)
      .gte('position.date', start)
      .lte('position.date', end)
      .order('id')
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    if (count !== null) total = count;
    const batch = (data ?? []) as unknown as Raw[];
    raw.push(...batch);
    if (batch.length < PAGE_SIZE) break;
  }
  if (total !== null && raw.length !== total) {
    throw new Error(
      `Lectura incompleta de asignaciones: ${raw.length} de ${total}. NO crees el cierre con `
      + 'estos datos: el bono se decidiría con un denominador incompleto.',
    );
  }
  const fechas = new Map<string, Set<string>>();
  for (const r of raw) {
    if (!r.position) continue;
    const set = fechas.get(r.worker_id) ?? new Set<string>();
    set.add(r.position.date);
    fechas.set(r.worker_id, set);
  }
  return new Map([...fechas].map(([w, f]) => [w, f.size]));
}

/**
 * Monto del bono por asistencia perfecta (`app_settings.bono_asistencia_monto`).
 *
 * Devuelve 0 si no está configurado, y 0 significa «esta instalación no tiene bono»: el cálculo
 * entonces no lo menciona en ninguna nota. Es mejor que un valor por defecto inventado, que
 * pagaría un bono que nadie prometió.
 *
 * El monto es global, pero no todas las campañas pagan bono: `campaigns.paga_bono_asistencia`
 * (0267). GARRA no lo paga (decisión de Neri, 6-oct): sin esto, como sus puestos existen solo los
 * días con marca, nadie falta nunca y todos lo habrían cobrado.
 */
export async function getBonoAsistencia(campaignId: string): Promise<number> {
  const [ajuste, campania] = await Promise.all([
    supabase.from('app_settings').select('value').eq('key', 'bono_asistencia_monto').maybeSingle(),
    supabase.from('campaigns').select('paga_bono_asistencia').eq('id', campaignId).maybeSingle(),
  ]);
  if (ajuste.error) throw ajuste.error;
  if (campania.error) throw campania.error;
  if ((campania.data as { paga_bono_asistencia: boolean } | null)?.paga_bono_asistencia === false) {
    return 0;
  }
  const n = Number((ajuste.data as { value: unknown } | null)?.value ?? 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export async function createClosure(
  campaignId: string, start: string, end: string, lines: PayrollLineCalc[],
): Promise<string> {
  const payload = lines.map((l) => ({
    worker_id: l.workerId,
    days_worked: l.daysWorked,
    hours_worked: l.hoursWorked,
    amount: l.amount,
    bono: l.bono,
    feriado: l.feriado,
    rate_amount: l.rateAmount,
    rate_breakdown: l.rateBreakdown,
    notes: l.notes,
  }));
  const { data, error } = await supabase.rpc('create_closure', {
    p_campaign_id: campaignId, p_start: start, p_end: end, lines: payload,
  });
  if (error) throw error;
  return data as string;
}

export interface ClosureListItem {
  id: string;
  period_start: string;
  period_end: string;
  status: 'borrador' | 'aprobado';
  created_at: string;
  approved_at: string | null;
  campaign: { id: string; client: string; brand: string; analytic_account: string | null } | null;
}

export async function listClosures(): Promise<ClosureListItem[]> {
  const { data, error } = await supabase
    .from('payroll_closures')
    .select('id, period_start, period_end, status, created_at, approved_at, campaign:campaigns(id, client, brand, analytic_account)')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as ClosureListItem[];
}

export interface ClosureLineRow {
  id: string;
  worker_id: string;
  days_worked: number;
  hours_worked: number;
  amount: number;
  /** Bono de asistencia, ya incluido en `amount`. La planilla lo pide en su propia columna. */
  bono: number;
  /** Lo que agregó el feriado, ya incluido en `amount` (0393). La planilla lo pide en su propia columna. */
  feriado: number;
  /** Tarifa mayoritaria del periodo; `null` si dos empatan o si se pagó por hora (0081). */
  rate_amount: number | null;
  /** `[[tarifa, días]]` de más días a menos. Resuelve el empate en la planilla. */
  rate_breakdown: [number, number][];
  notes: string[];
  worker: { dni: string; first_names: string; last_names: string } | null;
}

export interface ClosureDetail {
  closure: ClosureListItem;
  lines: ClosureLineRow[];
}

export async function getClosure(id: string): Promise<ClosureDetail> {
  const { data: closure, error: closureError } = await supabase
    .from('payroll_closures')
    .select('id, period_start, period_end, status, created_at, approved_at, campaign:campaigns(id, client, brand, analytic_account)')
    .eq('id', id)
    .single();
  if (closureError) throw closureError;

  const { data: lines, error: linesError } = await supabase
    .from('payroll_lines')
    .select('id, worker_id, days_worked, hours_worked, amount, bono, feriado, rate_amount, rate_breakdown, notes, worker:profiles(dni, first_names, last_names)')
    .eq('closure_id', id);
  if (linesError) throw linesError;

  const rows = ((lines ?? []) as unknown as ClosureLineRow[]).map((l) => ({
    ...l,
    days_worked: Number(l.days_worked),
    hours_worked: Number(l.hours_worked),
    amount: Number(l.amount),
    bono: Number(l.bono ?? 0),
    feriado: Number(l.feriado ?? 0),
    rate_amount: l.rate_amount === null ? null : Number(l.rate_amount),
    rate_breakdown: (l.rate_breakdown ?? []).map(
      ([t, d]) => [Number(t), Number(d)] as [number, number],
    ),
    notes: l.notes ?? [],
  }));
  rows.sort((a, b) => (a.worker?.last_names ?? '').localeCompare(b.worker?.last_names ?? ''));

  return { closure: closure as unknown as ClosureListItem, lines: rows };
}

/** Aprueba el cierre (definitivo): la RPC lo vuelve inmutable y cierra las posiciones. */
export async function approveClosure(id: string): Promise<void> {
  const { error } = await supabase.rpc('approve_closure', { closure_id: id });
  if (error) throw error;
}

/** Fila de `get_closure_payment_data`: los datos bancarios de un recurso del cierre. */
export interface ClosurePaymentRow {
  worker_id: string;
  document_type: string;
  dni: string;
  first_names: string;
  last_names: string;
  bank: string | null;
  account_number: string | null;
  cci: string | null;
  /** Supervisor de campo, deducido de las tiendas donde tuvo asignación (0080). `null` si ninguna. */
  supervisor_name: string | null;
  /** `true` si trabajó tiendas de más de un supervisor: va a un solo bloque y se anota. */
  mixed: boolean;
}

/**
 * Banco, cuenta y CCI de todos los recursos de un cierre, en una sola llamada auditada (0078).
 *
 * NO se usa `get_worker_payment_data` en bucle a propósito: serían 27 llamadas y 27 filas de
 * auditoría idénticas para una sola exportación. Esta deja una fila que dice qué cierre se exportó.
 */
export async function getClosurePaymentData(
  closureId: string,
): Promise<Map<string, ClosurePaymentRow>> {
  const { data, error } = await supabase.rpc('get_closure_payment_data', {
    p_closure_id: closureId,
  });
  if (error) throw error;
  const rows = (data ?? []) as unknown as ClosurePaymentRow[];
  return new Map(rows.map((r) => [r.worker_id, r]));
}
